import './SnapshotCreationDeviceSettings.css';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { useTable } from 'components/Table/model/useTable';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import {
  type NodeId,
  type NodeSn,
  type TableColumn,
  createTextColumn,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Badge } from 'components/Badge/Badge';
import { Button } from 'components/Button/Button';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import { StateListItem } from 'components/StateList/StateListItem';
import { TableView } from 'components/Table/components/TableView';
import { setSelectedRows } from 'components/Table/model/commands/setSelectedRows';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import type { AppTranslations } from 'translations/translations';
import { distinct } from 'utils/array/distinct';
import { SnapshotCreationSelectVendor } from './SnapshotCreationSelectVendor';
import type {
  TaskApiSource,
  TaskApiSourceMap,
  TaskSettingsState,
  VendorsSettingsId,
} from './SnapshotCreationTable';
import {
  type RequestErrors,
  isRequestIpErrors,
  isRequestSnErrors,
} from './useSnapshotCreation';

export interface SourceSelectEvent {
  deviceSn: NodeSn;
  settingsId: VendorsSettingsId;
}

export interface ContinueDiscoveryEvent {
  acceptedDeviceSns?: NodeSn[];
  notSelectedDeviceSns?: NodeSn[];
}

export interface SnapshotCreationDeviceSettingsRowData {
  family: string;
  hostname: string;
  id: NodeId;
  isApiTask: boolean;
  isSelected: boolean;
  parentId: NodeId;
  settingsStates: TaskSettingsState[];
  sn: NodeSn;
  source: TaskApiSource;
  vendor: string;
}

interface Props {
  failedData: SnapshotCreationDeviceSettingsRowData[];
  onBackBtnClick: () => void;
  onContinueDiscovery: (event: ContinueDiscoveryEvent) => void;
  onSnapshotSettingsOpen: (settingsTab: string) => void;

  onSourceSelect: (event: SourceSelectEvent) => void;
  requestErrors: RequestErrors;
  selectedSns: NodeSn[];
  sendingDataState: LoadingState;

  taskApiSourceMap: TaskApiSourceMap | null;
}

export interface SnapshotCreationOptionType {
  label: TaskApiSource;
  value: VendorsSettingsId;
}

const SHOW_MAX_MISSING_SNS = 50;

const hasNoRemainingValidSelectedRows = (
  selectedSns: NodeSn[],
  invalidDevicesLength: number,
  notSelectedDeviceSns: NodeSn[],
) =>
  // All user-selected table rows are invalid
  selectedSns.length === invalidDevicesLength &&
  // and user unchecked items to fix (rows in current table)
  notSelectedDeviceSns.length === invalidDevicesLength;

const makeRenderSettingsStates =
  (msg: AppTranslations) =>
  (rowData: SnapshotCreationDeviceSettingsRowData) => {
    if (
      rowData.settingsStates.includes('unreachableIp') &&
      rowData.settingsStates.includes('noCredentials')
    ) {
      return (
        <StateListItem type='negative'>
          {msg.snapshotCreation.unreachableIpAndNoCredentials}
        </StateListItem>
      );
    }
    if (rowData.settingsStates.includes('noCredentials')) {
      return (
        <StateListItem type='negative'>
          {msg.snapshotCreation.noCredentials}
        </StateListItem>
      );
    }
    if (rowData.settingsStates.includes('unreachableIp')) {
      return (
        <StateListItem type='negative'>
          {msg.snapshotCreation.unreachableIp}
        </StateListItem>
      );
    }

    if (rowData.settingsStates.includes('ok')) {
      return (
        <StateListItem type='positive'>
          {msg.snapshotCreation.hasSettings}
        </StateListItem>
      );
    }

    if (rowData.settingsStates.includes('noApiSettings')) {
      return (
        <StateListItem type='negative'>
          {msg.snapshotCreation.noApiSettings}
        </StateListItem>
      );
    }

    return (
      <StateListItem type='negative'>
        {msg.snapshotCreation.unknownStatus}
      </StateListItem>
    );
  };

const makeRenderActions =
  (props: Props, msg: AppTranslations) =>
  (rowData: SnapshotCreationDeviceSettingsRowData) => {
    if (
      !rowData.settingsStates.includes('noApiSettings') ||
      !(rowData.source in msg.discoverySettings.vendorsApi.vendorTypes)
    ) {
      // Current only possible content is "Select Vendor API" button.
      // This ensures that table auto sizing reserves width for the button,
      // because if it is not visible in first rendered rows, the column
      // is calculated too narrow and the button is cut off when scrolled onto.
      return <div className='w-28' />;
    }

    const options =
      (props.taskApiSourceMap?.[rowData.source]?.map((option) => ({
        label: option.label,
        value: option.id,
      })) as SnapshotCreationOptionType[]) ?? [];

    return (
      <SnapshotCreationSelectVendor
        options={options}
        onSourceSelect={(settingsId) => {
          props.onSourceSelect({
            deviceSn: rowData.sn,
            settingsId,
          });
        }}
      />
    );
  };

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
export const SnapshotCreationDeviceSettings: FC<Props> = (props) => {
  const { requestErrors } = props;
  const msg = useLocale();

  const failedDataColumns: TableColumn<SnapshotCreationDeviceSettingsRowData>[] =
    useMemo(
      () => [
        createTextColumn({
          key: 'hostname',
          title: msg.snapshotCreation.columns.hostname.name,
        }),
        createTextColumn({
          key: 'vendor',
          title: msg.snapshotCreation.columns.vendor.name,
        }),
        createTextColumn({
          key: 'family',
          title: msg.snapshotCreation.columns.family.name,
        }),
        createFrontendOnlyColumn({
          key: 'status',
          title: msg.snapshotCreation.columns.settingsStates.name,
          render: makeRenderSettingsStates(msg),
        }),
        createFrontendOnlyColumn({
          key: 'action',
          title: msg.snapshotCreation.columns.action.name,
          render: makeRenderActions(props, msg),
        }),
      ],
      [props, msg],
    );

  useEffect(() => {
    failedDataTable.dispatch(setSelectedRows, {
      rowIds: props.failedData.map(prop('sn')),
    });
  }, [props.failedData]);

  const failedDataTable = useTable({
    bulkActions: [], // just to show selection checkboxes
    columns: failedDataColumns,
    data: props.failedData,
    rowIdField: 'sn',
    title: msg.snapshotCreation.deviceSettings.title,
    loadRowIdsForSelectAll: 'fromData',
  });

  const notSelectedDeviceSns = useMemo(
    () =>
      props.failedData
        .map(prop('sn'))
        .filter((sn) => !failedDataTable.selectedRows.has(sn)),
    [failedDataTable.selectedRows, props.failedData],
  );

  const handleContinueDiscovery = () => {
    props.onContinueDiscovery(
      isRequestSnErrors(requestErrors) &&
        (requestErrors.errors.missingSns?.length ?? 0) > 0
        ? {
            acceptedDeviceSns: requestErrors.errors.missingSns,
          }
        : {
            notSelectedDeviceSns,
          },
    );
  };

  const openDeviceCredentials = useStableCallback(() => {
    props.onSnapshotSettingsOpen('deviceCredentials');
  });

  const openSettingsDiscovery = useStableCallback(() => {
    props.onSnapshotSettingsOpen('discovery');
  });

  const missingCredentialsLength =
    requestErrors.errors.noCredentials?.length ?? 0;
  const wrongIpLength =
    (!isRequestIpErrors(requestErrors)
      ? requestErrors.errors.unreachableIp?.length
      : 0) ?? 0;
  const missingVendorSettingsLength =
    (!isRequestIpErrors(requestErrors)
      ? requestErrors.errors.noApiSettings?.length
      : 0) ?? 0;
  const notIncludedLength =
    (isRequestIpErrors(requestErrors)
      ? requestErrors.errors.notIncluded?.length
      : 0) ?? 0;
  const excludedLength =
    (isRequestIpErrors(requestErrors)
      ? requestErrors.errors.excluded?.length
      : 0) ?? 0;
  const invalidDevicesLength = isRequestIpErrors(requestErrors)
    ? distinct([
        ...(requestErrors.errors.noCredentials ?? []),
        ...(requestErrors.errors.notIncluded ?? []),
        ...(requestErrors.errors.excluded ?? []),
      ]).length
    : distinct([
        ...(requestErrors.errors.noCredentials ?? []),
        ...(requestErrors.errors.unreachableIp ?? []),
        ...(requestErrors.errors.noApiSettings ?? []),
        ...(requestErrors.errors.missingSns ?? []),
      ]).length;
  const missingSnsLength =
    (isRequestSnErrors(requestErrors)
      ? requestErrors.errors.missingSns?.length
      : 0) ?? 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: there are some extra deps, not sure how they were meant to work
  const disabledContinueButton = useMemo(
    () =>
      props.sendingDataState === 'LOADING' ||
      (missingSnsLength === 0 &&
        (!props.failedData.length ||
          hasNoRemainingValidSelectedRows(
            props.selectedSns,
            invalidDevicesLength,
            notSelectedDeviceSns,
          ) ||
          props.failedData.some(
            (row) =>
              !row.settingsStates.includes('ok') &&
              !notSelectedDeviceSns.includes(row.sn),
          ))),
    [
      props.failedData,
      props.sendingDataState,
      props.selectedSns,
      invalidDevicesLength,
      notSelectedDeviceSns,
      missingSnsLength,
      missingCredentialsLength,
      wrongIpLength,
      missingVendorSettingsLength,
      notIncludedLength,
      excludedLength,
    ],
  );

  const getDescription = () => {
    if (missingSnsLength > 0) {
      return msg.snapshotCreation.deviceSettings.descriptionMissingSns;
    }
    return props.failedData.length
      ? msg.snapshotCreation.deviceSettings.descriptionForTable
      : msg.snapshotCreation.deviceSettings.description;
  };

  return (
    <>
      <p className='snapshot-creation-device-settings__description mb-5'>
        <FormattedMessage
          id='snapshotCreation.deviceSettings.description'
          defaultMessage={getDescription()}
          values={{ count: invalidDevicesLength }}
        />
      </p>
      <ul className='snapshot-creation-device-settings__errors-list'>
        {missingCredentialsLength > 0 && (
          <li>
            <FormattedMessage
              id='snapshotCreation.deviceSettings.missingCredentials'
              defaultMessage={
                msg.snapshotCreation.deviceSettings.missingCredentials
              }
              values={{ count: missingCredentialsLength }}
            />
            <ButtonAsA className='ml-1' onClick={openDeviceCredentials}>
              {msg.snapshotCreation.deviceSettings.fixItHere}
            </ButtonAsA>
          </li>
        )}
        {wrongIpLength > 0 && (
          <li>
            <FormattedMessage
              id='snapshotCreation.deviceSettings.wrongIp'
              defaultMessage={msg.snapshotCreation.deviceSettings.wrongIp}
              values={{ count: wrongIpLength }}
            />
            <ButtonAsA className='ml-1' onClick={openSettingsDiscovery}>
              {msg.snapshotCreation.deviceSettings.fixItHere}
            </ButtonAsA>
          </li>
        )}
        {missingVendorSettingsLength > 0 && (
          <li>
            <FormattedMessage
              id='snapshotCreation.deviceSettings.missingVendorSettings'
              defaultMessage={
                msg.snapshotCreation.deviceSettings.missingVendorSettings
              }
              values={{ count: missingVendorSettingsLength }}
            />
          </li>
        )}
        {isRequestSnErrors(requestErrors) && missingSnsLength > 0 && (
          // This should not happen under normal circumstances when everything works as intended,
          // because all device dependencies should be resolved before the first discovery attempt is fired.
          // But it was already coded and the workflow works if I try to break standard dependency resolution
          // (use deviceSns instead of allDevicesToRefresh in SnapshotCreationTable.tsx:refreshDevices),
          // so we keep it there, but in a simple form without a table.
          <li>
            {msg.snapshotCreation.deviceSettings.missingSns}{' '}
            {requestErrors.errors.missingSns
              ?.slice(0, SHOW_MAX_MISSING_SNS)
              .map((sn) => (
                <Badge key={sn} className='mr-1'>
                  {sn}
                </Badge>
              ))}
            {missingSnsLength > SHOW_MAX_MISSING_SNS && (
              <span className='text-gray-800'>
                {msg.snapshotCreation.deviceSettings.missingSnsMore.replace(
                  '{number}',
                  (missingSnsLength - SHOW_MAX_MISSING_SNS).toString(),
                )}
              </span>
            )}
          </li>
        )}
        {notIncludedLength > 0 && (
          <li>
            <FormattedMessage
              id='snapshotCreation.deviceSettings.notIncluded'
              defaultMessage={msg.snapshotCreation.deviceSettings.notIncluded}
              values={{ count: notIncludedLength }}
            />
            <ButtonAsA className='ml-1' onClick={openSettingsDiscovery}>
              {msg.snapshotCreation.deviceSettings.fixItHere}
            </ButtonAsA>
          </li>
        )}
        {excludedLength > 0 && (
          <li>
            <FormattedMessage
              id='snapshotCreation.deviceSettings.excluded'
              defaultMessage={msg.snapshotCreation.deviceSettings.excluded}
              values={{ count: excludedLength }}
            />
            <ButtonAsA className='ml-1' onClick={openSettingsDiscovery}>
              {msg.snapshotCreation.deviceSettings.fixItHere}
            </ButtonAsA>
          </li>
        )}
      </ul>
      {/* Refresh button not needed if missing SNs table is visible */}
      {missingSnsLength === 0 && (
        <div className='mb-10'>
          <Button
            variant='secondary'
            onClick={handleContinueDiscovery}
            type='button'
            icon='fa-arrows-rotate'
            loading={props.sendingDataState === 'LOADING'}
          >
            {msg.snapshotCreation.deviceSettings.btnRefresh}
          </Button>
        </div>
      )}
      {props.failedData.length > 0 && (
        <TableView
          borderless
          className='-mx-8 mb border-y border-default flex-1'
          model={failedDataTable}
        />
      )}
      <div className='flex justify-end'>
        <Button
          className='mr-2'
          onClick={props.onBackBtnClick}
          size='sm'
          type='button'
          variant='secondary'
        >
          {msg.snapshotCreation.btnPreviousStep}
        </Button>
        <Button
          disabled={disabledContinueButton}
          onClick={handleContinueDiscovery}
          size='sm'
          type='button'
          variant='primary'
          loading={props.sendingDataState === 'LOADING'}
        >
          {props.sendingDataState === 'LOADING'
            ? msg.snapshotCreation.waitStarting
            : msg.snapshotCreation.btnNextStep}
        </Button>
      </div>
    </>
  );
};
